import React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useUser } from "../state";
import { API_Theme, API_Voters } from "../../../src/apidefs";
import { api } from "../api";
import LightBox from "../DesignSystem/LightBox";
import { RegisterOrLoginPrompt } from "../Auth/RegisterOrLoginPrompt";
import { Page } from "../DesignSystem/formatting/TwoColumnView";
import { Button } from "../DesignSystem/Button";
import { SmallButton } from "../DesignSystem/SmallButton";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  PhotoIcon,
  TrashIcon,
  NoSymbolIcon,
  EyeIcon,
} from "@heroicons/react/24/solid";

export type ThemeVoteType = 1 | -1 | 0;

type ThemeSuggestParams = { id: string };

type VoteData = {
  upVoted: number[];
  downVoted: number[];
};

const INITIAL_VOTE_DATA: VoteData = { upVoted: [], downVoted: [] };

export const ThemeSuggestPage = (): JSX.Element => {
  const { id } = useParams<ThemeSuggestParams>();
  const userState = useUser();
  const user = userState.type === "logged-in" ? userState.user : undefined;
  const [updateState, setUpdateState] = useState<"updating" | "done">("done");
  const [themes, setThemes] = useState<API_Theme>();
  const [inputValue, setInputValue] = useState("");
  const [imgInputValue, setImgInputValue] = useState("");
  const [uploadedUrl, setUploadedUrl] = useState<string>();
  const [lightBoxVisible, setLightBoxVisible] = useState(false);
  const [imgLightBoxUrl, setImgLightBoxUrl] = useState<string>();
  const [altUsername, setAltUsername] = useState("");
  const [voters, setVoters] = useState<API_Voters>([]);
  const [voteData, setVoteData] = useState<VoteData>(INITIAL_VOTE_DATA);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isBugTracker = Number(id) === 2;

  const fetchVotes = useCallback(async () => {
    const votes = await api.get("/api/vote/theme");

    if (api.isToast(votes)) {
      setVoteData(INITIAL_VOTE_DATA);
      return;
    }

    const upVoted: number[] = [];
    const downVoted: number[] = [];

    for (const { vote, theme_id } of votes || []) {
      if (vote === 1) {
        upVoted.push(theme_id);
      } else if (vote === -1) {
        downVoted.push(theme_id);
      }
    }

    setVoteData({ upVoted, downVoted });
  }, []);

  const fetchThemes = useCallback(async () => {
    const result = await api.get<"/api/theme/:id">(`/api/theme/${id}`);
    if (!api.isToast(result)) {
      setThemes(result);
    }
  }, [id]);

  useEffect(() => {
    fetchThemes();
    fetchVotes();
  }, [fetchThemes, fetchVotes]);

  const getAllVotersForCompo = async () => {
    const result = await api.get<"/api/vote/voters/:id">(
      `/api/vote/voters/${id}`
    );
    setVoters(result);
  };

  const toggleLightBox = (themePic?: string) => {
    setLightBoxVisible((visible) => !visible);
    setImgLightBoxUrl(themePic);
  };

  const getSignedRequest = (file: File) => {
    const xhr = new XMLHttpRequest();
    xhr.open(
      "GET",
      "/api/sign-s3?file-name=" + file.name + "&file-type=" + file.type
    );

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          uploadFile(file, response.signedRequest, response.url);
        } else {
          alert(
            "Upload failed for some reason. Ensure you're uploading an image."
          );
        }
      }
    };

    xhr.send();
  };

  const uploadFile = (file: File, signedRequest: string, url: string) => {
    const xhr = new XMLHttpRequest();

    setUpdateState("updating");
    xhr.open("PUT", signedRequest);

    xhr.onreadystatechange = () => {
      setUpdateState("done");

      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          setUploadedUrl(url);
        } else {
          alert("Could not upload file.");
        }
      }
    };
    xhr.send(file);
  };

  const changeFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = fileInputRef.current?.files;
    const file = files && files[0];
    setImgInputValue(event.target.value);

    if (file) {
      getSignedRequest(file);
    }
  };

  const submit = async () => {
    setUpdateState("updating");

    await api.post("/api/theme", {
      theme: inputValue,
      compo_id: Number(id),
      uploaded_url: uploadedUrl,
    });

    setUpdateState("done");
    setInputValue("");
    setUploadedUrl(undefined);
    setImgInputValue("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    await fetchThemes();
    await fetchVotes();
  };

  const vote = async (themeId: number, voteValue: ThemeVoteType) => {
    let calcVote = voteValue;
    if (voteValue === 1 && voteData.upVoted.includes(themeId)) {
      calcVote = 0;
    } else if (voteValue === -1 && voteData.downVoted.includes(themeId)) {
      calcVote = 0;
    }

    await api.post("/api/vote/theme", {
      vote: calcVote,
      theme_id: themeId,
    });

    await fetchVotes();
    await fetchThemes();
  };

  const deleteTheme = async (theme: string, banned: { banned: boolean }) => {
    await api.post("/api/theme/delete", {
      theme,
      banned: banned.banned,
    });

    await fetchThemes();
  };

  const deleteVotes = async (username: string) => {
    await api.post<"/api/vote/delete/:id">(`/api/vote/delete/${id}`, {
      username,
    });

    fetchThemes();
    fetchVotes();
  };

  const themeList = useMemo(() => {
    if (!themes) return [];
    const hidden = false;
    return hidden ? themes.sort(() => Math.random() - 0.5) : themes;
  }, [themes]);

  if (!themes) {
    return <span> Loading! </span>;
  }

  return (
    <div>
      <div onClick={() => toggleLightBox()}>
        <LightBox visible={lightBoxVisible} imgLightBoxUrl={imgLightBoxUrl} />
      </div>

      <Page>
        <div className="mx-auto max-w-3xl">
          <h1 className="mb-2 text-3xl font-bold text-slate-900 dark:text-white">
            {isBugTracker ? "Bugs & Requests" : "Suggest A Theme"}
          </h1>
          <p className="mb-8 text-slate-500 dark:text-slate-400">
            {isBugTracker
              ? "Submit bugs and feature requests here. Vote on the ones that you want fixed sooner!"
              : "Submit and vote on cool themes. The winning theme will be the theme of the next Chips compo. Unless it's really stupid, then the mods will do whatever they want."}
          </p>

          {user ? (
            <div className="mb-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-800">
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
                {isBugTracker ? "Bug / feature request" : "Theme"}
              </label>
              <input
                type="text"
                onChange={(event) => setInputValue(event.currentTarget.value)}
                placeholder={
                  themes.length === 0
                    ? "thonk"
                    : `e.g. ${themes[Math.floor(themes.length * Math.random())]?.theme || ""}`
                }
                value={inputValue}
                className="mb-4 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-500 dark:focus:border-amber-400 dark:focus:ring-amber-400"
              />

              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1">
                  <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-slate-500 dark:text-slate-400">
                    <PhotoIcon className="h-4 w-4" />
                    Image (optional)
                  </label>
                  <input
                    type="file"
                    id="file-input"
                    onChange={changeFileInput}
                    ref={fileInputRef}
                    value={imgInputValue}
                    className="text-sm text-slate-500 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700 file:transition hover:file:bg-slate-200 dark:text-slate-400 dark:file:bg-slate-700 dark:file:text-slate-300 dark:hover:file:bg-slate-600"
                  />
                  <input
                    type="hidden"
                    id="uploaded-file-url"
                    name="uploaded-file-url"
                    value={uploadedUrl}
                  />
                </div>
                <Button
                  variant="primary"
                  onClick={submit}
                  disabled={
                    inputValue === "" ||
                    inputValue.length > 100 ||
                    updateState === "updating"
                  }
                  isLoading={updateState === "updating"}
                  grow={false}
                >
                  Submit
                </Button>
              </div>
            </div>
          ) : (
            <div className="mb-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-800">
              <RegisterOrLoginPrompt doWhat="add a theme" />
            </div>
          )}

          <div className="space-y-3">
            {themeList.map(({ score, theme, theme_pic, id: themeId }) => {
              const upvoted = voteData.upVoted.indexOf(themeId) > -1;
              const downvoted = voteData.downVoted.indexOf(themeId) > -1;

              return (
                <div
                  key={theme}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition-all hover:shadow-md dark:border-white/10 dark:bg-slate-800 dark:hover:border-white/20"
                >
                  <div className="flex flex-col items-center gap-0.5">
                    <button
                      onClick={() => vote(themeId, 1)}
                      className={`rounded p-0.5 transition ${
                        upvoted
                          ? "text-amber-500"
                          : "text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                      }`}
                    >
                      <ChevronUpIcon className="h-5 w-5" />
                    </button>
                    <span
                      className={`text-sm font-semibold ${
                        upvoted
                          ? "text-amber-500"
                          : downvoted
                            ? "text-slate-700 dark:text-slate-300"
                            : "text-slate-500 dark:text-slate-400"
                      }`}
                    >
                      {score}
                    </span>
                    <button
                      onClick={() => vote(themeId, -1)}
                      className={`rounded p-0.5 transition ${
                        downvoted
                          ? "text-slate-700 dark:text-slate-300"
                          : "text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                      }`}
                    >
                      <ChevronDownIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {theme_pic && (
                    <img
                      src={theme_pic}
                      className="h-10 w-10 cursor-pointer rounded-lg object-cover"
                      onClick={() => toggleLightBox(theme_pic)}
                    />
                  )}

                  <span className="min-w-0 flex-1 text-sm font-medium text-slate-800 dark:text-slate-100">
                    {theme}
                  </span>

                  {user && user.admin && (
                    <div className="ml-auto flex items-center gap-1.5">
                      <SmallButton
                        variant="default"
                        onClick={() => deleteTheme(theme, { banned: false })}
                        icon={<TrashIcon className="h-3.5 w-3.5" />}
                      >
                        Delete
                      </SmallButton>
                      <SmallButton
                        variant="mod"
                        onClick={() => deleteTheme(theme, { banned: true })}
                        icon={<NoSymbolIcon className="h-3.5 w-3.5" />}
                      >
                        Ban
                      </SmallButton>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {user && user.admin && (
            <div className="mt-8 rounded-xl border border-violet-300/50 bg-violet-50 p-5 dark:border-violet-500/30 dark:bg-violet-900/20">
              <h3 className="mb-3 text-sm font-semibold text-violet-700 dark:text-violet-300">
                Mod Tools
              </h3>
              <div className="mb-4">
                <Button
                  variant="admin"
                  onClick={getAllVotersForCompo}
                  grow={false}
                  icon={<EyeIcon className="h-4 w-4" />}
                >
                  Show voters
                </Button>
                {voters.length > 0 && (
                  <div className="mt-3 space-y-1 text-sm text-violet-700 dark:text-violet-300">
                    {voters.map((voter) => (
                      <div key={voter.username}>
                        {voter.username}: {voter.count}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-violet-700 dark:text-violet-300">
                  Delete all votes from a user for this compo
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={altUsername}
                    onChange={(e) => setAltUsername(e.target.value)}
                    placeholder="username"
                    className="rounded-lg border border-violet-300 bg-white px-3 py-1.5 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500 dark:border-violet-500/30 dark:bg-slate-800 dark:text-white dark:placeholder-slate-500"
                  />
                  <Button
                    variant="danger"
                    onClick={() => deleteVotes(altUsername)}
                    grow={false}
                    icon={<TrashIcon className="h-4 w-4" />}
                  >
                    Delete votes
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Page>
    </div>
  );
};
